import { EmptyBase, NonEmptyBase, } from '@rimbu/collection-types/map-custom';
import { Stream } from '@rimbu/stream';
export class BiMultiMapEmpty extends EmptyBase {
    constructor(context) {
        super();
        this.context = context;
    }
    get keyValueMultiMap() {
        return this.context.keyValueMultiMapContext.empty();
    }
    get valueKeyMultiMap() {
        return this.context.valueKeyMultiMapContext.empty();
    }
    get keySize() {
        return 0;
    }
    streamKeys() {
        return Stream.empty();
    }
    streamValues() {
        return Stream.empty();
    }
    hasKey() {
        return false;
    }
    hasValue() {
        return false;
    }
    hasEntry() {
        return false;
    }
    add(key, value) {
        return this.context.createNonEmpty(this.context.keyValueMultiMapContext.of([key, value]), this.context.valueKeyMultiMapContext.of([value, key]));
    }
    addEntries(entries) {
        return this.context.from(entries);
    }
    setValues(key, values) {
        return this.context.from(Stream.from(values).map((value) => [key, value]));
    }
    setKeys(value, keys) {
        return this.context.from(Stream.from(keys).map((key) => [key, value]));
    }
    getValues() {
        return this.context.keyValueMultiMapContext.keyMapValuesContext.empty();
    }
    getKeys() {
        return this.context.valueKeyMultiMapContext.keyMapValuesContext.empty();
    }
    removeKey() {
        return this;
    }
    removeKeys() {
        return this;
    }
    removeEntry() {
        return this;
    }
    removeEntries() {
        return this;
    }
    removeValue() {
        return this;
    }
    removeValues() {
        return this;
    }
    toString() {
        return `${this.context.typeTag}()`;
    }
    toJSON() {
        return {
            dataType: this.context.typeTag,
            value: [],
        };
    }
    toBuilder() {
        return this.context.builder();
    }
}
export class BiMultiMapNonEmpty extends NonEmptyBase {
    constructor(context, keyValueMultiMap, valueKeyMultiMap) {
        super();
        this.context = context;
        this.keyValueMultiMap = keyValueMultiMap;
        this.valueKeyMultiMap = valueKeyMultiMap;
    }
    assumeNonEmpty() {
        return this;
    }
    asNormal() {
        return this;
    }
    get keySize() {
        return this.keyValueMultiMap.keySize;
    }
    get size() {
        return this.keyValueMultiMap.size;
    }
    stream() {
        return this.keyValueMultiMap.stream();
    }
    streamKeys() {
        return this.keyValueMultiMap.streamKeys();
    }
    streamValues() {
        return this.valueKeyMultiMap.streamKeys();
    }
    hasKey(key) {
        return this.keyValueMultiMap.hasKey(key);
    }
    hasValue(key) {
        return this.valueKeyMultiMap.hasKey(key);
    }
    hasEntry(key, value) {
        return this.hasKey(key) && this.hasValue(value);
    }
    add(key, value) {
        const newKeyValueMultiMap = this.keyValueMultiMap.add(key, value);
        if (newKeyValueMultiMap === this.keyValueMultiMap)
            return this;
        const newValueKeyMultiMap = this.valueKeyMultiMap.add(value, key);
        return this.context.createNonEmpty(newKeyValueMultiMap, newValueKeyMultiMap);
    }
    addEntries(entries) {
        const builder = this.toBuilder();
        builder.addEntries(entries);
        return builder.build();
    }
    setValues(key, values) {
        const builder = this.toBuilder();
        builder.setValues(key, values);
        return builder.build();
    }
    setKeys(value, keys) {
        const builder = this.toBuilder();
        builder.setKeys(value, keys);
        return builder.build();
    }
    getValues(key) {
        return this.keyValueMultiMap.getValues(key);
    }
    getKeys(value) {
        return this.valueKeyMultiMap.getValues(value);
    }
    removeKey(key) {
        const result = this.keyValueMultiMap.removeKeyAndGet(key);
        if (undefined === result) {
            return this;
        }
        const [newKeyValueMultiMap, oldValues] = result;
        if (!newKeyValueMultiMap.nonEmpty())
            return this.context.empty();
        const newValueKeyMultiMap = this.valueKeyMultiMap
            .removeEntries(oldValues.stream().map((value) => [value, key]))
            .assumeNonEmpty();
        return this.context.createNonEmpty(newKeyValueMultiMap, newValueKeyMultiMap);
    }
    removeKeys(keys) {
        const builder = this.toBuilder();
        builder.removeKeys(keys);
        return builder.build();
    }
    removeValue(value) {
        const result = this.valueKeyMultiMap.removeKeyAndGet(value);
        if (undefined === result) {
            return this;
        }
        const [newValueKeyMultiMap, oldKeys] = result;
        if (!newValueKeyMultiMap.nonEmpty())
            return this.context.empty();
        const newKeyValueMultiMap = this.keyValueMultiMap
            .removeEntries(oldKeys.stream().map((key) => [key, value]))
            .assumeNonEmpty();
        return this.context.createNonEmpty(newKeyValueMultiMap, newValueKeyMultiMap);
    }
    removeValues(values) {
        const builder = this.toBuilder();
        builder.removeValues(values);
        return builder.build();
    }
    removeEntry(key, value) {
        const newKeyValueMultiMap = this.keyValueMultiMap.removeEntry(key, value);
        if (newKeyValueMultiMap === this.keyValueMultiMap)
            return this;
        if (!newKeyValueMultiMap.nonEmpty())
            return this.context.empty();
        const newValueKeyMultiMap = this.valueKeyMultiMap
            .removeEntry(value, key)
            .assumeNonEmpty();
        return this.context.createNonEmpty(newKeyValueMultiMap, newValueKeyMultiMap);
    }
    removeEntries(entries) {
        const builder = this.toBuilder();
        builder.removeEntries(entries);
        return builder.build();
    }
    forEach(f, options = {}) {
        this.keyValueMultiMap.forEach(f, options);
    }
    filter(pred, options = {}) {
        const builder = this.context.builder();
        builder.addEntries(this.stream().filter(pred, options));
        if (builder.size === this.size)
            return this;
        return builder.build();
    }
    toArray() {
        return this.keyValueMultiMap.toArray();
    }
    toString() {
        return this.keyValueMultiMap.streamKeys().join({
            start: `${this.context.typeTag}(`,
            sep: ', ',
            end: ')',
            valueToString: (key) => {
                return `${key} <-> ${this.keyValueMultiMap
                    .getValues(key)
                    .stream()
                    .join({ start: '(', sep: ', ', end: ')' })}`;
            },
        });
    }
    toJSON() {
        return {
            dataType: this.context.typeTag,
            value: this.keyValueMultiMap.toJSON().value,
        };
    }
    toBuilder() {
        return this.context.createBuilder(this);
    }
}
//# sourceMappingURL=immutable.mjs.map